import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { AuthResponseDto, ReadUserDto } from "@package/types";
import { useChatsStore } from "./chats.store";
import { useModelsStore } from "./models.store";
import { useMessagesStore } from "./messages.store";
import { useProjectsStore } from "./projects.store";
import type { AsyncState } from "./types";
import { useUsersStore } from "./users.store";
import { useWordExportStore } from "./word-export.store";

interface AuthState extends AsyncState {
  user: ReadUserDto | null;
  token: string | null;
  isAuthenticated: boolean;
}

interface AuthActions {
  setUser: (user: AuthResponseDto) => void;
  setLoading: (loading: boolean) => void;
  logout: () => void;
  validate: () => Promise<void>;
}

type AuthStore = AuthState & AuthActions;

const initialState: AuthState = {
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: false,
  isLoaded: false,
  error: null
};

export const useAuthStore = create<AuthStore>()(
  persist(
    (set) => ({
      ...initialState,

      setUser: (user: AuthResponseDto) =>
        set({
          user,
          token: user.token,
          isAuthenticated: true,
          isLoading: false,
          error: null
        }),

      setLoading: (loading: boolean) =>
        set({
          isLoading: loading,
          ...(loading && { error: null })
        }),

      logout: () => {
        useChatsStore.getState().reset();
        useModelsStore.getState().reset();
        useMessagesStore.getState().reset();
        useProjectsStore.getState().reset();
        useUsersStore.getState().reset();
        useWordExportStore.getState().reset();

        set({
          ...initialState
        });
      },

      validate: async () => {
        const { token } = useAuthStore.getState();
        if (!token) return;

        // Lazy import avoids circular dependency between api.ts and auth.store.ts.
        const { api } = await import("@/lib/api");
        try {
          const user = await api.get<ReadUserDto>("/auth/me");
          set({ user });
        } catch {
          // 401 responses are handled inside api.ts (logout + redirect),
          // other errors are ignored to avoid logging out on transient failures.
        }
      }
    }),
    {
      name: "auth-storage",
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated
      })
    }
  )
);
